import { createFileRoute } from "@tanstack/react-router";
import { JsonLd } from "@/components/json-ld";
import { DriversSection } from "@/components/landing/drivers-section";
import { FeaturesSection } from "@/components/landing/features-section";
import { Footer } from "@/components/landing/footer";
import { HeroSection } from "@/components/landing/hero-section";
import { PricingSection } from "@/components/landing/pricing-section";
import { StatsSection } from "@/components/landing/stats-section";
import { canonicalLink, seo } from "@/utils/seo";
import {
  faqJsonLd,
  organizationJsonLd,
  subscriptionProductsJsonLd,
  webSiteJsonLd,
} from "@/utils/structured-data";

const COMING_SOON = false;

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      ...seo({
        title: "Setups by SZESE — Pro iRacing Setups from World-Class Sim Racers",
        description:
          "Download professional iRacing car setups crafted by SZESE E-Sport drivers. Weekly race, qualifying & endurance setups for every iRacing series. Free and premium tiers available.",
        keywords:
          "iRacing setups, sim racing setups, iRacing car setup download, SZESE E-Sport, pro iRacing setups, race setup, qualifying setup, iRacing season setups",
        canonical: "https://setupsbyszese.com",
      }),
    ],
    links: [canonicalLink("https://setupsbyszese.com")],
  }),
  component: COMING_SOON ? ComingSoonPage : HomePage,
});

function FolkDivider() {
  return (
    <div className="relative py-4">
      <img
        src="/kalocsai-strip.webp"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="mx-auto w-full max-w-5xl h-auto opacity-[0.09]"
      />
    </div>
  );
}

function ComingSoonPage() {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute inset-0">
        <img
          src="/kalocsai-corner-tr.webp"
          alt=""
          aria-hidden="true"
          className="absolute top-0 right-0 w-[400px] h-[400px] object-contain"
          style={{
            opacity: 0.1,
            maskImage: "radial-gradient(ellipse at top right, black 30%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at top right, black 30%, transparent 70%)",
          }}
        />
        <img
          src="/kalocsai-corner-bl.webp"
          alt=""
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-[350px] h-[350px] object-contain"
          style={{
            opacity: 0.08,
            maskImage: "radial-gradient(ellipse at bottom left, black 30%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at bottom left, black 30%, transparent 70%)",
          }}
        />
      </div>
      <img
        src="/logo.png"
        alt="Setups by SZESE"
        className="relative z-10 mb-8 h-32 drop-shadow-lg"
      />
      <p className="relative z-10 mb-6 max-w-md text-center text-lg text-muted-foreground">
        Professional iRacing setups from world-class sim racers.
      </p>
      <div className="relative z-10 rounded-full border border-folk-red/30 bg-folk-red/10 px-6 py-2 text-sm font-medium text-folk-red">
        Coming Soon
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen bg-background">
      <JsonLd data={organizationJsonLd()} />
      <JsonLd data={webSiteJsonLd()} />
      <JsonLd data={subscriptionProductsJsonLd()} />
      <JsonLd
        data={faqJsonLd([
          {
            question: "What are iRacing setups?",
            answer:
              "iRacing setups are car configuration files (.sto) that adjust suspension, aero, tire pressures, and other settings to optimize your car's performance on specific tracks. Our setups are crafted by professional sim racers for each week's race.",
          },
          {
            question: "How often are new setups released?",
            answer:
              "New setups are released every week to match iRacing's rotating schedule. Each week includes setups for race, qualifying, and sometimes endurance configurations.",
          },
          {
            question: "What's the difference between Free, Plus, and Premium?",
            answer:
              "Free users get a limited selection of sample setups each week. Plus ($8/mo) subscribers get all weekly setups for their chosen series. Premium ($15/mo) subscribers get all setups across every series, plus replay files (.rpy) and telemetry data (.ibt).",
          },
          {
            question: "Who creates the setups?",
            answer:
              "Our setups are created by the drivers of SZESE E-Sport, the sim racing team of Széchenyi István University in Győr, Hungary. The team includes world-class sim racers competing at the highest levels of iRacing.",
          },
          {
            question: "Can I use the setups in my iRacing sessions?",
            answer:
              "Yes! Simply download the .sto setup file and place it in your iRacing setups folder. The setup will then be available to load in the iRacing garage before any session.",
          },
        ])}
      />
      <main>
        <HeroSection />
        <FolkDivider />
        <StatsSection />
        <FolkDivider />
        <FeaturesSection />
        <DriversSection />
        <FolkDivider />
        <PricingSection />
      </main>
      <Footer />
    </div>
  );
}
